/**
 * Technical Calculator Utility (Frontend).
 *
 * Primary purpose: Dual Technical & Execution scoring engine per trade.
 * 1. Setup Score (S_setup):
 *    Calculated from Market Vision structural selections (stored in trade.view JSON) + planned R:R.
 *    Stored in database column: trades.setup_score.
 *
 * 2. Management Score (S_mgmt):
 *    Calculated from Real Exit Price (realized R) combined with selected Exit Reason.
 *    Stored in database column: trades.management_score.
 *
 * 3. Macro Confidence derivation from bias_calculator.
 */

export const CATEGORY_WEIGHTS = {
    // Macro
    "horizon_middle": 10.0,
    "horizon_long": 10.0,
    "horizon_short": 6.0,
    "macro_vol_expansion": 6.0,
    "macro_vol_compression": 4.0,
    "macro_type_marubozu": 5.0,
    "macro_type_momentum": 5.0,
    "macro_type_engulfing": 5.0,
    "bias_vol_expansion": 5.0,
    "bias_type_momentum": 4.0,
    "bias_type_marubozu": 4.0,
    "bias_type_engulfing": 4.0,

    // Bias
    "bias_cycle_expansion": 12.0,
    "bias_cycle_consolidation": 6.0,
    "bias_trend_uptrend": 15.0,
    "bias_trend_downtrend": 15.0,
    "bias_struct_range": 8.0,
    "bias_pq_trend_strong": 6.0,
    "bias_pq_mom_strong": 6.0,

    // Observation Zone
    "obs_scenario_continuation": 12.0,
    "obs_scenario_flip": 10.0,
    "obs_scenario_breakout": 8.0,
    "ret_62_79": 10.0,
    "ret_greater_05": 6.0,
    "ret_less_05": 3.0,
    "obs_liq_valid": 10.0,
    "obs_liq_invalid": 5.0,
    "obs_liq_decisive": 6.0,
    "obs_liq_rejection_wick": 6.0,
    "obs_liq_pin_bars": 5.0,
    "obs_liq_multi_wicks": 4.0,
    "obs_liq_tweezers": 4.0,

    // Confirmation
    "conf_shift_choch": 10.0,
    "conf_shift_break_retest": 8.0,
    "conf_cp_hs": 6.0,
    "conf_cp_double": 6.0,

    // Entry Model
    "entry_trigger_full_body_close": 10.0,
    "entry_candle_marubozu": 8.0,
    "entry_candle_momentum": 8.0,
    "entry_candle_engulfing": 8.0,
};

export const EXIT_REASON_WEIGHTS = {
    "full take profit": 50.0,
    "tp + rejection": 48.0,
    "partial take profit": 45.0,
    "trailing stop hit": 48.0,
    "protective stop hit": 46.0,
    "breakeven exit": 44.0,
    "structure broken": 46.0,
    "stop loss hit": 40.0,
    "strong rejection": 44.0,
    "multiple rejections": 42.0,
    "failed breakout": 40.0,
    "major news / event shift": 42.0,
    "end of week": 42.0,
    "pre-news": 40.0,
    "trade took too long": 35.0,
    "execution mistake": 10.0,
    "emotional exit": 10.0,
    "premature exit": 10.0,
};

export const CONCEPT_RULES = [
    // [concept_name, [keywords], weight]
    ["Middle/Long Horizon", ["middle", "long", "horizon_middle", "horizon_long"], 10.0],
    ["Short Horizon", ["short", "horizon_short"], 6.0],
    ["Macro Expansion", ["macro_vol_expansion", "macro.*expansion", "candle.*expansion"], 6.0],
    ["Macro Directional Candle", ["macro_type_marubozu", "macro_type_momentum", "macro_type_engulfing"], 5.0],
    ["Cycle Expansion", ["bias_cycle_expansion", "cycle.*expansion"], 12.0],
    ["Cycle Consolidation", ["bias_cycle_consolidation", "consolidation"], 6.0],
    ["Trend Uptrend", ["bias_trend_uptrend", "uptrend", "bullish"], 15.0],
    ["Trend Downtrend", ["bias_trend_downtrend", "downtrend", "bearish"], 15.0],
    ["Range Structure", ["bias_struct_range", "range"], 8.0],
    ["Price Quality Strong", ["bias_pq_trend_strong", "bias_pq_mom_strong", "strong"], 8.0],
    ["Pullback Continuation", ["obs_scenario_continuation", "pullback", "continuation"], 12.0],
    ["Flip Zone Reversal", ["obs_scenario_flip", "flip zone", "flip"], 10.0],
    ["Range Expansion", ["obs_scenario_breakout", "range expansion", "expansion"], 8.0],
    ["62–79% Retracement Zone (Pullback)", ["ret_62_79", "62–79%", "62-79", "ote", "pullback"], 10.0],
    ["Retracement >= 50%", ["ret_greater_05", "≥ 50%", ">= 50%"], 6.0],
    ["Valid Liquidity Event (Stop Run)", ["obs_liq_valid", "valid", "sweep", "stop_run", "stop run"], 10.0],
    ["Opposite Liquidity Event", ["obs_liq_invalid", "opposite liquidity event"], 5.0],
    ["Rejection Wick / Decisive", ["obs_liq_decisive", "rejection wick", "wick"], 6.0],
    ["Structural Shift", ["conf_shift_choch", "choch", "minor shift", "structural shift", "shift"], 10.0],
    ["Break & Retest", ["conf_shift_break_retest", "break & retest"], 8.0],
    ["Chart Pattern (H&S / Double)", ["conf_cp_hs", "conf_cp_double", "head & shoulders", "double top"], 6.0],
    ["Full-Body Close Trigger", ["entry_trigger_full_body_close", "full-body close", "full_body", "full body"], 10.0],
    ["Impulsive Entry Candle", ["marubozu", "momentum", "engulfing"], 8.0],
];

export function calculateSetupScore(viewData, rrPlanned = null) {
    let viewText = "";
    if (typeof viewData === "object" && viewData !== null) {
        viewText = JSON.stringify(viewData).toLowerCase();
    } else if (typeof viewData === "string") {
        viewText = viewData.toLowerCase();
    }

    let matchedPoints = 0.0;
    const matchedNotes = [];

    CONCEPT_RULES.forEach(([name, keywords, weight]) => {
        if (keywords.some(kw => viewText.includes(kw))) {
            matchedPoints += weight;
            matchedNotes.push(`${name} (+${weight})`);
        }
    });

    const isViewEmpty = matchedNotes.length === 0;
    const baseScore = isViewEmpty ? 45.0 : Math.min(85.0, (matchedPoints / 90.0) * 85.0);

    if (isViewEmpty) {
        matchedNotes.push("Aucune donnée Market Vision (JSON vide) (+45)");
    }

    let rrPts = 0.0;
    if (rrPlanned && rrPlanned > 0) {
        if (rrPlanned >= 3.0) rrPts = 15.0;
        else if (rrPlanned >= 2.5) rrPts = 12.0;
        else if (rrPlanned >= 2.0) rrPts = 9.0;
        else if (rrPlanned >= 1.5) rrPts = 5.0;
    }

    const finalScore = Number(Math.min(100.0, Math.max(10.0, baseScore + rrPts)).toFixed(1));

    let grade = "C";
    if (finalScore >= 85.0) grade = "A+";
    else if (finalScore >= 72.0) grade = "A";
    else if (finalScore >= 58.0) grade = "B";

    return {
        score: finalScore,
        grade,
        rr_planned: rrPlanned ? Number(rrPlanned.toFixed(2)) : null,
        matched_count: isViewEmpty ? 0 : matchedNotes.length,
        notes: matchedNotes.slice(0, 8)
    };
}

export function calculateManagementScore(tradeOrExit = {}, entryArg, slArg, tpArg, sideArg, exitReasonArg) {
    let tradeRow = {};
    if (typeof tradeOrExit === 'object' && tradeOrExit !== null && !entryArg) {
        tradeRow = tradeOrExit;
    } else {
        tradeRow = {
            exit: tradeOrExit,
            entry: entryArg,
            sl: slArg,
            tp: tpArg,
            side: sideArg,
            exit_reason: exitReasonArg,
            exitReason: exitReasonArg
        };
    }

    const parseNum = (v) => {
        if (v === null || v === undefined) return null;
        if (typeof v === "number") return v;
        const cleaned = String(v).replace(/[^0-9.-]/g, "");
        const n = parseFloat(cleaned);
        return isNaN(n) ? null : n;
    };

    const entryP = parseNum(tradeRow.entry);
    const slP = parseNum(tradeRow.sl);
    const tpP = parseNum(tradeRow.tp);
    const exitP = parseNum(tradeRow.exit);
    const isLong = !String(tradeRow.side || "BUY").toUpperCase().includes("SHORT") && !String(tradeRow.side || "BUY").toUpperCase().includes("SELL");

    const hasSetup = Boolean(entryP && slP && entryP !== slP);
    const riskDist = hasSetup ? Math.abs(entryP - slP) : 0.0;

    const rrPlanned = (hasSetup && tpP && riskDist > 0)
        ? Math.abs(tpP - entryP) / riskDist
        : (parseNum(tradeRow.rr_planned) || 2.5);

    let rrRealized = null;
    if (hasSetup && exitP !== null && riskDist > 0) {
        rrRealized = isLong ? (exitP - entryP) / riskDist : (entryP - exitP) / riskDist;
    } else {
        rrRealized = parseNum(tradeRow.rr_realized);
    }

    // Component A: Price Execution (0 - 50)
    let priceScore = 25.0;
    const notes = [];
    if (rrRealized !== null) {
        if (rrPlanned > 0 && rrRealized >= 0.90 * rrPlanned) {
            priceScore = 50.0;
            notes.push(`Objectif TP atteint (${rrRealized.toFixed(2)}R sur ${rrPlanned.toFixed(2)}R)`);
        } else if (rrRealized >= 1.5) {
            priceScore = 42.0;
            notes.push(`Gain substantiel sécurisé (${rrRealized.toFixed(2)}R)`);
        } else if (rrRealized >= 0.8) {
            priceScore = 36.0;
            notes.push(`Gain modéré sécurisé (${rrRealized.toFixed(2)}R)`);
        } else if (rrRealized > 0.15) {
            priceScore = 30.0;
            notes.push(`Gain partiel sécurisé (${rrRealized.toFixed(2)}R)`);
        } else if (rrRealized >= -0.15) {
            priceScore = 28.0;
            notes.push("Sortie Breakeven (0R) : Capital préservé");
        } else if (rrRealized >= -1.1) {
            priceScore = 25.0;
            notes.push(`Stop Loss respecté (${rrRealized.toFixed(2)}R)`);
        } else {
            priceScore = 5.0;
            notes.push(`⚠️ Stop dépassé (${rrRealized.toFixed(2)}R)`);
        }
    }

    // Component B: Exit Reason Discipline (0 - 50)
    const reasonRaw = String(tradeRow.exit_reason || tradeRow.exitReason || "").toLowerCase().trim();
    let reasonScore = 25.0;
    let reasonLabel = "Non spécifiée";

    if (reasonRaw) {
        let matched = false;
        for (const [keyPhrase, pts] of Object.entries(EXIT_REASON_WEIGHTS)) {
            if (reasonRaw.includes(keyPhrase)) {
                reasonScore = pts;
                reasonLabel = keyPhrase;
                matched = true;
                break;
            }
        }
        if (!matched) {
            reasonScore = 30.0;
            reasonLabel = reasonRaw;
        }
    } else {
        reasonScore = 20.0;
    }

    const finalScore = Number(Math.min(100.0, Math.max(10.0, priceScore + reasonScore)).toFixed(1));

    let grade = "POOR";
    if (rrRealized === null && exitP === null) grade = "PENDING";
    else if (finalScore >= 85.0) grade = "EXCELLENT";
    else if (finalScore >= 70.0) grade = "GOOD";
    else if (finalScore >= 50.0) grade = "AVERAGE";

    return {
        score: finalScore,
        grade,
        r_planned: Number(rrPlanned.toFixed(2)),
        r_realized: rrRealized !== null ? Number(rrRealized.toFixed(2)) : null,
        exit_reason: reasonLabel,
        notes
    };
}

export function calculateTradeExpectancy(setupScore, managementScore = null, rrPlanned = 2.5, rrRealized = null, wBaseline = 0.50) {
    if (setupScore === null || setupScore === undefined || isNaN(setupScore)) {
        return { setup_score: 0, management_score: null, e_r_planned: 0.0, e_r_realized: 0.0, leakage_r: 0.0 };
    }

    const sNorm = setupScore > 1.0 ? setupScore / 100.0 : setupScore;
    const pWin = Math.min(0.85, Math.max(0.15, wBaseline + (sNorm - 0.65) * 0.50));
    const pLoss = 1.0 - pWin;

    const eRPlanned = Number(((pWin * rrPlanned) - (pLoss * 1.0)).toFixed(2));

    // Realized Expectancy:
    // If rrRealized is known (from actual prices), use it directly.
    // If only managementScore is known (theoretical simulation), scale planned R by mgmtFactor.
    let eRRealized = eRPlanned;
    if (rrRealized !== null && rrRealized !== undefined) {
        eRRealized = Number(Number(rrRealized).toFixed(2));
    } else if (managementScore !== null && managementScore !== undefined) {
        const mgmtFactor = managementScore / 100.0;
        eRRealized = Number(((pWin * (rrPlanned * mgmtFactor)) - (pLoss * 1.0)).toFixed(2));
    }

    const leakageR = Number((eRRealized - eRPlanned).toFixed(2));

    return {
        setup_score: setupScore,
        management_score: managementScore,
        e_r_planned: eRPlanned,
        e_r_realized: eRRealized,
        leakage_r: leakageR,
        formula: `E[R] = (${pWin.toFixed(2)} × ${rrPlanned}R) - (${pLoss.toFixed(2)} × 1.0R) = ${eRPlanned >= 0 ? "+" : ""}${eRPlanned}R`,
        verdict: `Leakage: ${leakageR >= 0 ? "+" : ""}${leakageR.toFixed(2)} R`
    };
}

export const calculateExpectancy = calculateTradeExpectancy;

/**
 * Derives a normalized Macro Confidence Score (0.40 to 0.95) directly from bias_calculator metrics:
 * Fully aligned with Python bias_calculator.py V10:
 * - Magnitude across horizons (Short x1, Middle x2, Long x3)
 * - Directional alignment (unanimous trend vs Steamroller pullbacks vs dangerous churn)
 * - Supporting economic catalyst depth
 */
export function deriveMacroConfidence(moData) {
    if (!moData) return 0.50;
    // If moData already carries a valid confidence_score float (e.g. 0.85, 0.75)
    if (moData.confidence_score !== undefined && moData.confidence_score !== null && moData.confidence_score !== '') {
        const val = parseFloat(moData.confidence_score);
        if (!isNaN(val) && val > 0) {
            return val > 1.0 ? Number((val / 100).toFixed(2)) : Number(val.toFixed(2));
        }
    }
    if (moData.confidenceScore !== undefined && moData.confidenceScore !== null && moData.confidenceScore !== '') {
        const val = parseFloat(moData.confidenceScore);
        if (!isNaN(val) && val > 0) {
            return val > 1.0 ? Number((val / 100).toFixed(2)) : Number(val.toFixed(2));
        }
    }

    // Deterministic calculation identical to Python bias_calculator V10
    const s_st = parseFloat(moData.score_short_term || moData.scoreShortTerm || 0) || 0;
    const s_mt = parseFloat(moData.score_middle_term || moData.scoreMiddleTerm || 0) || 0;
    const s_lt = parseFloat(moData.score_long_term || moData.scoreLongTerm || 0) || 0;
    const events_count = parseInt(moData.events_count || moData.eventsCount || moData.valid_events_count || moData.validEventsCount || 0, 10) || 0;

    const magnitude = Math.abs(s_st) * 1.0 + Math.abs(s_mt) * 1.5 + Math.abs(s_lt) * 2.0;

    const is_steamroller = Math.abs(s_lt) >= 8 && (s_mt * s_lt >= 0);
    const is_conflict = (s_st !== 0 && s_lt !== 0) ? (s_st * s_lt < 0) : false;

    // Distinguish desired institutional pullback (Steamroller Buy the Dip / Sell the Rally)
    // from dangerous structural churn (conflicts without solid higher-timeframe backing).
    const is_desired_pullback = (
        is_steamroller &&
        is_conflict &&
        (s_mt * s_lt > 0 || Math.abs(s_st) <= 8) &&
        Math.abs(s_st) < Math.abs(s_lt)
    );
    const is_dangerous_churn = is_conflict && !is_desired_pullback;

    let effectiveMagnitude = magnitude;
    let alignmentBonus = 0.0;
    const active = [s_st, s_mt, s_lt].filter(s => s !== 0);

    if (is_desired_pullback) {
        effectiveMagnitude = magnitude;
        alignmentBonus = 0.10;
    } else if (is_dangerous_churn) {
        const damped = Math.max(0.0, magnitude - (Math.abs(s_st) + Math.abs(s_lt)) * 1.5);
        effectiveMagnitude = damped;
        const conflictScale = Math.min(0.15, (Math.abs(s_st) + Math.abs(s_lt)) / 100.0);
        alignmentBonus = -(0.10 + conflictScale);
    } else if (active.length >= 2 && (active.every(s => s > 0) || active.every(s => s < 0))) {
        effectiveMagnitude = magnitude;
        alignmentBonus = 0.12;
    } else {
        effectiveMagnitude = magnitude;
        alignmentBonus = 0.0;
    }

    const volBonus = Math.min(0.08, Math.max(0, events_count) * 0.02);
    const baseConfidence = 0.50 + Math.min(0.35, effectiveMagnitude / 100.0);
    const finalScore = baseConfidence + alignmentBonus + volBonus;
    return Number(Math.min(0.95, Math.max(0.40, finalScore)).toFixed(2));
}

/**
 * Computes global mathematical expectancy across all portfolio trades:
 * - Realized Expectancy (R): sum(R_realized) / count(closed_trades)
 * - Planned Expectancy (R): mean(E[R]_planned) derived from setup quality and planned R:R
 */
export function calculatePortfolioExpectancy(trades = []) {
    let totalRealizedR = 0;
    let countRealized = 0;
    let totalPlannedR = 0;
    let countPlanned = 0;

    const parseNum = (v) => {
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        const cleaned = String(v).replace(/[^0-9.-]/g, '');
        const n = parseFloat(cleaned);
        return isNaN(n) ? null : n;
    };

    // Pass 1: Baseline Historical Win Rate with Bayesian Shrinkage (k=20, prior=0.50)
    // Eliminates small sample distortions while converging asymptotically to real win rate:
    let totalWins = 0;
    let totalClosed = 0;
    trades.forEach(trade => {
        const isClosed = trade.status === 'Done';
        if (isClosed) {
            const pnl = parseNum(trade.pnl);
            if (pnl !== null) {
                totalClosed++;
                if (pnl > 0) totalWins++;
            } else {
                const entry = parseNum(trade.entry);
                const sl = parseNum(trade.sl);
                const exit = parseNum(trade.exit);
                if (entry !== null && sl !== null && exit !== null && entry !== sl) {
                    totalClosed++;
                    const isLong = !String(trade.side || 'BUY').toUpperCase().includes('SELL') &&
                                   !String(trade.side || 'BUY').toUpperCase().includes('SHORT');
                    const r = isLong ? (exit - entry) / Math.abs(entry - sl) : (entry - exit) / Math.abs(entry - sl);
                    if (r > 0.05) totalWins++;
                }
            }
        }
    });

    const SHRINKAGE_K = 20.0;
    const wBaseline = (totalWins + SHRINKAGE_K * 0.50) / (totalClosed + SHRINKAGE_K);

    trades.forEach(trade => {
        const isClosed = trade.status === 'Done';

        const entry = parseNum(trade.entry);
        const sl = parseNum(trade.sl);
        const tp = parseNum(trade.tp);
        const exit = parseNum(trade.exit);

        if (entry !== null && sl !== null && entry !== sl) {
            const risk = Math.abs(entry - sl);

            // Realized R
            if (isClosed && exit !== null) {
                const isLong = !String(trade.side || 'BUY').toUpperCase().includes('SELL') &&
                               !String(trade.side || 'BUY').toUpperCase().includes('SHORT');
                const rRealized = isLong ? (exit - entry) / risk : (entry - exit) / risk;
                totalRealizedR += rRealized;
                countRealized++;
            }

            // Planned Expectancy in R
            let plannedRR = 2.0;
            if (tp !== null) {
                plannedRR = Math.abs(tp - entry) / risk;
            } else if (trade.rr_planned) {
                plannedRR = parseNum(trade.rr_planned) || 2.0;
            }

            // Planned Expectancy in R: Driven strictly by Pure Ex-Ante Setup Score S_setup
            // Immune to post-entry exit discipline / management score contamination (no look-ahead bias)
            let setupScore = null;
            if (trade.view) {
                try {
                    const sRes = calculateSetupScore(trade.view, plannedRR);
                    if (sRes) setupScore = sRes.score;
                } catch (e) {}
            }
            if (setupScore === null && trade.setup_score !== undefined && trade.setup_score !== null) {
                setupScore = Number(trade.setup_score);
            }
            if (setupScore === null && trade.setupScore !== undefined && trade.setupScore !== null) {
                setupScore = Number(trade.setupScore);
            }
            const rawScore = (setupScore !== null && Number(setupScore) > 0) ? Number(setupScore) : 65.0;
            const score = (rawScore > 0 && rawScore <= 1.0) ? rawScore * 100.0 : rawScore;

            const sNorm = score / 100.0;
            const pWin = Math.min(0.85, Math.max(0.15, wBaseline + (sNorm - 0.65) * 0.50));
            const pLoss = 1.0 - pWin;
            const eRPlanned = (pWin * plannedRR) - (pLoss * 1.0);

            totalPlannedR += eRPlanned;
            countPlanned++;
        }
    });

    const expectancyRRealized = countRealized > 0 ? Number((totalRealizedR / countRealized).toFixed(2)) : 0.0;
    const expectancyRPlanned = countPlanned > 0 ? Number((totalPlannedR / countPlanned).toFixed(2)) : null;

    return {
        expectancy_r_realized: expectancyRRealized,
        expectancy_r_planned: expectancyRPlanned,
        total_r: Number(totalRealizedR.toFixed(2)),
        count_realized: countRealized,
        count_planned: countPlanned
    };
}

